// The tool policy matrix (spec 14 §4.3, §2.4, D-509 rulings 1 and 11, D-512
// ruling 7, D-507 ruling 5): one row per tool on the agent's own list plus its
// orphaned rows. The autonomy select offers only values at or stricter than
// the tool's `max_autonomy`, never an autonomous value for an irreversible
// tool, and a value laxer than the row's own only to `agents.config.manage`.
// Changes collect in an unsaved bar with a diff summary; a save that meets a
// newer row shows theirs beside yours.

import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { toast } from "../../../../components/toast";
import { requestIdOf } from "../../../../lib/request";
import { minorUnitDigits, normalizeTypedDecimal } from "../../sales/lib/money";
import {
  agentsKeys,
  dismissPolicy,
  problemCode,
  putPolicy,
  useAgentFacilities,
  useAgentPolicies,
  useAgentTools,
} from "../hooks/agents-queries";
import {
  allowedAutonomies,
  policyParamsShape,
  spendCapBlocksPolicy,
  type AgentDetail,
  type AgentPolicyItem,
  type AgentToolItem,
  type PolicyAutonomy,
} from "../lib/agents-lib";
import {
  draftChanges,
  paramsOf,
  rowDraftOf,
  type NumbersDraft,
  type RowDraft,
} from "../lib/policy-draft";
import { errorText, toolName } from "../lib/labels";

interface Conflict {
  readonly toolCode: string;
  readonly mine: RowDraft;
}

const AUTONOMOUS_VALUES: readonly PolicyAutonomy[] = [
  "threshold",
  "autonomous_with_cap",
  "autonomous",
];

export function PolicyMatrix({
  agent,
  canManage,
  canConfig,
}: {
  agent: AgentDetail;
  canManage: boolean;
  canConfig: boolean;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const policies = useAgentPolicies(agent.id);
  const tools = useAgentTools();
  const facilities = useAgentFacilities();
  const [drafts, setDrafts] = useState<ReadonlyMap<string, RowDraft>>(new Map());
  const [rowErrors, setRowErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<Conflict | undefined>(undefined);
  const currency = agent.caps.spend_cap_currency;
  const digits = minorUnitDigits(currency, i18n.language);
  const normalize = (text: string) => normalizeTypedDecimal(text, i18n.language);

  const toolsByCode = useMemo(
    () => new Map((tools.data ?? []).map((tool) => [tool.tool_code, tool])),
    [tools.data],
  );
  const facilityName = (id: string) => facilities.data?.find((f) => f.id === id)?.name ?? "";

  if (policies.isPending || tools.isPending) {
    return <TableSkeleton rows={6} cols={4} label={t("agents.detail.policies.loading")} />;
  }
  if (policies.isError || tools.isError) {
    return (
      <ErrorState
        title={t("agents.detail.policies.loadError")}
        retryLabel={t("agents.common.retry")}
        onRetry={() => {
          void policies.refetch();
          void tools.refetch();
        }}
        requestId={requestIdOf(policies.error ?? tools.error)}
        requestIdLabel={t("agents.common.requestId")}
      />
    );
  }

  const items = policies.data;
  const writes = items.filter((i) => i.kind === "write");
  const rows = [...writes, ...items.filter((i) => i.kind === "read")];
  const changes = draftChanges(items, drafts, digits);

  const draftOf = (item: AgentPolicyItem): RowDraft =>
    drafts.get(item.tool_code) ?? rowDraftOf(item, digits);
  const setDraft = (item: AgentPolicyItem, next: RowDraft) => {
    setDrafts((prev) => new Map(prev).set(item.tool_code, next));
    setRowErrors((prev) => {
      const out = new Map(prev);
      out.delete(item.tool_code);
      return out;
    });
  };
  const setNumbers = (item: AgentPolicyItem, numbers: NumbersDraft) => {
    setDraft(item, { ...draftOf(item), numbers });
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: agentsKeys.policies(agent.id) });

  const saveOne = async (item: AgentPolicyItem, draft: RowDraft, version: number | null) => {
    const tool = toolsByCode.get(item.tool_code);
    const shape = policyParamsShape(tool?.policy_params_schema ?? null);
    const params = paramsOf(shape, draft.numbers, currency, digits, normalize);
    if (!params.ok) {
      setRowErrors((prev) =>
        new Map(prev).set(item.tool_code, t("agents.detail.policies.invalidNumber")),
      );
      return false;
    }
    await putPolicy(agent.id, item.tool_code, {
      autonomy: draft.autonomy,
      ...(shape === "none" ? {} : { policy_params: params.params }),
      ...(version === null ? {} : { version }),
    });
    return true;
  };

  const save = async () => {
    setSaving(true);
    let saved = 0;
    try {
      for (const change of changes) {
        const item = items.find((i) => i.tool_code === change.toolCode);
        const draft = drafts.get(change.toolCode);
        if (item === undefined || draft === undefined) continue;
        try {
          if (await saveOne(item, draft, item.version)) {
            saved += 1;
            setDrafts((prev) => {
              const out = new Map(prev);
              out.delete(change.toolCode);
              return out;
            });
          }
        } catch (error) {
          if (problemCode(error) === "AGENT_POLICY_VERSION_CONFLICT") {
            await refresh();
            setConflict({ toolCode: change.toolCode, mine: draft });
            break;
          }
          setRowErrors((prev) => new Map(prev).set(change.toolCode, errorText(t, error)));
        }
      }
    } finally {
      setSaving(false);
      await refresh();
      if (saved > 0) toast.success(t("agents.detail.unsaved.saved", { count: saved }));
    }
  };

  const numbersEditor = (item: AgentPolicyItem, tool: AgentToolItem | undefined) => {
    const shape = policyParamsShape(tool?.policy_params_schema ?? null);
    if (shape === "none") return null;
    const draft = draftOf(item);
    // A facility cap moves only under agents.config.manage (D-526 ruling 2).
    const editable = !item.orphaned && (canConfig || (canManage && shape !== "facility_caps"));
    if (shape === "facility_caps") {
      const used = new Set(draft.numbers.facilities.map((f) => f.facilityId));
      return (
        <div className="ag-stack" data-testid="policy-facility-caps">
          {draft.numbers.facilities.length === 0 ? (
            <span className="ag-hint">{t("agents.detail.policies.noCaps")}</span>
          ) : null}
          {draft.numbers.facilities.map((f, index) => (
            <div key={f.facilityId} className="ag-policy-numbers">
              <Field
                label={t("agents.detail.policies.facilityCap", {
                  facility: facilityName(f.facilityId),
                  currency,
                })}
              >
                <input
                  className="ds-input ag-input-narrow"
                  inputMode="decimal"
                  value={f.cap}
                  disabled={!editable}
                  onChange={(e) => {
                    const facilitiesNext = draft.numbers.facilities.map((x, i) =>
                      i === index ? { ...x, cap: e.target.value } : x,
                    );
                    setNumbers(item, { ...draft.numbers, facilities: facilitiesNext });
                  }}
                  data-testid="policy-facility-cap"
                />
              </Field>
              {editable ? (
                <Button
                  onClick={() => {
                    setNumbers(item, {
                      ...draft.numbers,
                      facilities: draft.numbers.facilities.filter((_, i) => i !== index),
                    });
                  }}
                >
                  {t("agents.detail.policies.removeFacility")}
                </Button>
              ) : null}
            </div>
          ))}
          {editable && (facilities.data ?? []).some((f) => !used.has(f.id)) ? (
            <Field label={t("agents.detail.policies.addFacility")}>
              <Select
                value=""
                onChange={(e) => {
                  if (e.target.value === "") return;
                  setNumbers(item, {
                    ...draft.numbers,
                    facilities: [
                      ...draft.numbers.facilities,
                      { facilityId: e.target.value, cap: "" },
                    ],
                  });
                }}
                data-testid="policy-add-facility"
              >
                <option value="">{t("agents.detail.memberships.choose")}</option>
                {(facilities.data ?? [])
                  .filter((f) => !used.has(f.id))
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
              </Select>
            </Field>
          ) : null}
        </div>
      );
    }
    return (
      <div className="ag-policy-numbers">
        <Field label={t("agents.detail.policies.cap", { currency })}>
          <input
            className="ds-input ag-input-narrow"
            inputMode="decimal"
            value={draft.numbers.cap}
            disabled={!editable}
            onChange={(e) => {
              setNumbers(item, { ...draft.numbers, cap: e.target.value });
            }}
            data-testid="policy-cap"
          />
        </Field>
        {shape === "cap_reliability" ? (
          <Field label={t("agents.detail.policies.minReliability")}>
            <input
              className="ds-input ag-input-narrow"
              inputMode="numeric"
              value={draft.numbers.reliability}
              disabled={!editable}
              onChange={(e) => {
                setNumbers(item, { ...draft.numbers, reliability: e.target.value });
              }}
              data-testid="policy-min-reliability"
            />
          </Field>
        ) : null}
      </div>
    );
  };

  const columns: DataTableColumn<AgentPolicyItem>[] = [
    {
      key: "tool",
      header: t("agents.detail.policies.columns.tool"),
      phonePriority: 1,
      render: (item) => {
        const tool = toolsByCode.get(item.tool_code);
        return (
          <span className="ag-stack">
            <span
              className={item.orphaned ? "ag-struck" : undefined}
              data-testid="policy-tool-name"
            >
              {toolName(t, item.tool_code)}
            </span>
            <span className="ag-row">
              <StatusChip variant="neutral" dot={false}>
                {t(`agents.detail.policies.kind.${item.kind}`)}
              </StatusChip>
              {tool?.reversibility !== undefined && tool.reversibility !== null ? (
                <StatusChip
                  variant={tool.reversibility === "irreversible" ? "warn" : "neutral"}
                  dot={false}
                >
                  {t(`agents.detail.policies.reversibility.${tool.reversibility}`)}
                </StatusChip>
              ) : null}
              <span className="ag-hint">{t(`agents.detail.policies.source.${item.source}`)}</span>
            </span>
            {item.orphaned ? (
              <span className="ag-hint" data-testid="policy-orphaned">
                {t("agents.detail.policies.orphaned")}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "autonomy",
      header: t("agents.detail.policies.columns.autonomy"),
      phonePriority: 2,
      render: (item) => {
        const draft = draftOf(item);
        const tool = toolsByCode.get(item.tool_code);
        if (item.orphaned || !canManage) {
          return t(`agents.detail.autonomy.${item.autonomy}`);
        }
        // A read row offers its declared default and Disabled (§4.3's
        // Autonomy column, D-543 ruling 21).
        const options = allowedAutonomies({
          current: item.autonomy,
          max: item.max_autonomy,
          reversibility: tool?.reversibility ?? null,
          canConfig,
          kind: item.kind,
          declared: tool?.default_autonomy ?? item.max_autonomy,
        });
        return (
          <Select
            value={draft.autonomy}
            aria-label={`${t("agents.detail.policies.columns.autonomy")} ${toolName(t, item.tool_code)}`}
            onChange={(e) => {
              setDraft(item, { ...draft, autonomy: e.target.value as PolicyAutonomy });
            }}
            data-testid="policy-autonomy"
            data-tool={item.tool_code}
          >
            {options.map((value) => (
              <option key={value} value={value}>
                {t(`agents.detail.autonomy.${value}`)}
              </option>
            ))}
          </Select>
        );
      },
    },
    {
      key: "numbers",
      header: t("agents.detail.policies.columns.numbers"),
      phonePriority: 3,
      render: (item) => {
        const draft = draftOf(item);
        const tool = toolsByCode.get(item.tool_code);
        const shape = policyParamsShape(tool?.policy_params_schema ?? null);
        const params = paramsOf(shape, draft.numbers, currency, digits, normalize);
        const warn =
          params.ok &&
          AUTONOMOUS_VALUES.includes(draft.autonomy) &&
          spendCapBlocksPolicy(params.params, agent.caps.monthly_spend_cap_minor);
        const error = rowErrors.get(item.tool_code);
        return (
          <span className="ag-stack">
            {item.kind === "write" ? numbersEditor(item, tool) : null}
            {warn ? (
              <span className="ag-hint" data-testid="policy-spend-warning">
                {t("agents.detail.policies.spendCapWarning")}
              </span>
            ) : null}
            {error !== undefined ? (
              <span className="ag-error" role="alert" data-testid="policy-row-error">
                {error}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "usage",
      header: t("agents.detail.policies.columns.usage"),
      numeric: true,
      render: (item) =>
        t("agents.detail.policies.usage", {
          executed: item.usage_30d.executed.toLocaleString(i18n.language),
          proposed: item.usage_30d.proposed.toLocaleString(i18n.language),
        }),
    },
    {
      key: "dismiss",
      header: t("agents.detail.policies.dismiss"),
      actions: true,
      render: (item) =>
        item.orphaned && canManage ? (
          <Button
            onClick={() => {
              void dismissPolicy(agent.id, item.tool_code)
                .then(() => {
                  toast.success(t("agents.detail.policies.dismissed"));
                })
                .catch((error: unknown) => {
                  toast.error(errorText(t, error));
                })
                .finally(() => {
                  void refresh();
                });
            }}
            data-testid="policy-dismiss"
          >
            {t("agents.detail.policies.dismiss")}
          </Button>
        ) : null,
    },
  ];

  const conflictItem =
    conflict === undefined ? undefined : items.find((i) => i.tool_code === conflict.toolCode);

  return (
    <div className="ag-stack" data-testid="policy-matrix">
      {writes.length === 0 ? (
        <p className="ag-hint">{t("agents.detail.policies.readOnlyRows")}</p>
      ) : null}
      {changes.length > 0 ? (
        <div className="ag-unsaved" role="status" data-testid="policy-unsaved">
          <div className="ag-stack">
            <strong>{t("agents.detail.unsaved.count", { count: changes.length })}</strong>
            <ul>
              {changes.map((change) => (
                <li key={change.toolCode}>
                  {change.fromAutonomy !== change.toAutonomy
                    ? t("agents.detail.unsaved.item", {
                        tool: toolName(t, change.toolCode),
                        from: t(`agents.detail.autonomy.${change.fromAutonomy}`),
                        to: t(`agents.detail.autonomy.${change.toAutonomy}`),
                      })
                    : t("agents.detail.unsaved.itemNumbers", {
                        tool: toolName(t, change.toolCode),
                      })}
                </li>
              ))}
            </ul>
          </div>
          <div className="ag-row">
            <Button
              onClick={() => {
                setDrafts(new Map());
                setRowErrors(new Map());
              }}
              disabled={saving}
              data-testid="policy-discard"
            >
              {t("agents.common.discard")}
            </Button>
            <Button
              variant="primary"
              disabled={saving}
              onClick={() => {
                void save();
              }}
              data-testid="policy-save"
            >
              {saving ? t("agents.common.saving") : t("agents.common.save")}
            </Button>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(item) => item.tool_code}
        rowTestId="policy-row"
        empty={
          <EmptyState
            title={t("agents.detail.policies.empty")}
            body={t("agents.detail.policies.readOnlyRows")}
          />
        }
        clientPager={{
          range: (from, to, total) => t("agents.common.pageOf", { from, to, total }),
          prevLabel: t("agents.common.previous"),
          nextLabel: t("agents.common.next"),
        }}
        data-testid="policy-table"
      />
      {conflict !== undefined && conflictItem !== undefined ? (
        <Modal
          label={t("agents.detail.conflict.title")}
          heading={t("agents.detail.conflict.title")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setConflict(undefined);
          }}
          pending={saving}
          testId="policy-conflict"
        >
          <div className="ag-stack">
            <p className="ag-hint">{t("agents.detail.conflict.body")}</p>
            <p>
              <strong>{toolName(t, conflict.toolCode)}</strong>
            </p>
            <div className="ag-compare">
              <div className="ag-stack" data-testid="policy-conflict-theirs">
                <strong>{t("agents.detail.conflict.theirs")}</strong>
                <span>{t(`agents.detail.autonomy.${conflictItem.autonomy}`)}</span>
              </div>
              <div className="ag-stack" data-testid="policy-conflict-yours">
                <strong>{t("agents.detail.conflict.yours")}</strong>
                <span>{t(`agents.detail.autonomy.${conflict.mine.autonomy}`)}</span>
              </div>
            </div>
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setDrafts((prev) => {
                    const out = new Map(prev);
                    out.delete(conflict.toolCode);
                    return out;
                  });
                  setConflict(undefined);
                }}
                data-testid="policy-conflict-keep"
              >
                {t("agents.detail.conflict.keepTheirs")}
              </Button>
              <Button
                variant="primary"
                disabled={saving}
                onClick={() => {
                  setSaving(true);
                  void saveOne(conflictItem, conflict.mine, conflictItem.version)
                    .then((ok) => {
                      if (ok) {
                        setDrafts((prev) => {
                          const out = new Map(prev);
                          out.delete(conflict.toolCode);
                          return out;
                        });
                        toast.success(t("agents.detail.unsaved.saved", { count: 1 }));
                      }
                    })
                    .catch((error: unknown) => {
                      toast.error(errorText(t, error));
                    })
                    .finally(() => {
                      setSaving(false);
                      setConflict(undefined);
                      void refresh();
                    });
                }}
                data-testid="policy-conflict-apply"
              >
                {t("agents.detail.conflict.applyMine")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
